import React, { Component } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { fireTrackAnalytics } from '@atlassian/jira-analytics-web-react/src';
import { createServerSideValidationErrorsGetter } from '@atlassian/jira-common-directory-v2/src/utils/shareable-entity-dialog';
import log from '@atlassian/jira-common-util-logging/src/log';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { ValidationError } from '@atlassian/jira-fetch';
import type { EditableFilter } from '@atlassian/jira-filters-common/src/model';
import { withFlagService, type FlagService } from '@atlassian/jira-flags';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import {
	type OperationData,
	type OperationResult,
	ENTITY_NAME_FIELD_NAME,
	SHARE_PERMISSIONS_FIELD_NAME,
	EDIT_PERMISSIONS_FIELD_NAME,
} from '@atlassian/jira-shareable-entity-dialog/src';
import type { Intl } from '@atlassian/jira-shared-types';
import { editFilterErrorFlag, editFilterSuccessFlag } from '../flags';
import type { AppProps } from '../model';
import editFilter from '../services/edit';
import EditFiltersModal from './view';

type Props = AppProps &
	Intl & {
		flagService: FlagService;
		isAdmin: boolean;
	};

const getServerSideValidationErrors = createServerSideValidationErrorsGetter({
	filterName: ENTITY_NAME_FIELD_NAME,
	shares: SHARE_PERMISSIONS_FIELD_NAME,
	edit_permissions: EDIT_PERMISSIONS_FIELD_NAME,
});

// eslint-disable-next-line jira/react/no-class-components
export class EditModalWrapper extends Component<Props> {
	componentDidMount() {
		this.props.onInitiateSuccess();
	}

	onOperation = (filter: EditableFilter) => {
		const { id, isAdmin } = this.props;

		return editFilter(id, filter, isAdmin);
	};

	onOperationSucceed = (
		filter: OperationData,
		_: OperationResult<undefined>,
		analyticsEvent: UIAnalyticsEvent,
	) => {
		const { id, onEdited, flagService } = this.props;
		fireUIAnalytics(analyticsEvent, 'editedFilter', {
			isViewerPrivate: filter.sharePermissions.length === 0,
			isEditorPrivate: filter.editPermissions && filter.editPermissions.length === 0,
		});
		flagService.showFlag(editFilterSuccessFlag(filter.name));
		fireTrackAnalytics(analyticsEvent, {
			action: 'updated',
			actionSubjectId: `${id}`,
		});
		onEdited(filter);
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onOperationFailed = (error: any, analyticsEvent: UIAnalyticsEvent) => {
		const { id, onEditFailed, flagService } = this.props;

		log.safeErrorWithoutCustomerData(
			'directories.filters.edit-modal.submit',
			`edit filter has failed, id=${this.props.id}`,
			error,
		);

		if (!(error instanceof ValidationError)) {
			flagService.showFlag(editFilterErrorFlag());
		}

		onEditFailed();

		fireErrorAnalytics({
			event: analyticsEvent,
			error,
			meta: {
				id: 'operationFailedHandler',
				packageName: 'jiraEditFiltersModal',
				teamName: 'jira-cosmos',
			},
			attributes: {
				action: 'updated',
				actionSubjectId: `${id}`,
			},
		});
	};

	render() {
		const { baseUrl, onCancel, name, description, editPermissions, sharePermissions, intl } =
			this.props;

		return (
			<EditFiltersModal
				baseUrl={baseUrl}
				intl={intl}
				onCancel={onCancel}
				name={name}
				description={description}
				sharePermissions={sharePermissions}
				editPermissions={editPermissions}
				operation={this.onOperation}
				onOperationSucceed={this.onOperationSucceed}
				onOperationFailed={this.onOperationFailed}
				getServerSideValidationErrors={getServerSideValidationErrors}
			/>
		);
	}
}

export default withFlagService(injectIntl(EditModalWrapper));
